import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FilterConfig, FilterOption } from '@supply-mover-2-0/core';
import React, { useEffect, useRef } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Text } from '../base/Text';
import { DatePicker } from '../forms/DatePicker';

interface BaseDateRangeProps {
	filterType: string;
	config: FilterConfig;
	selectedValues: FilterOption[];
	onValuesChange: (values: FilterOption[]) => void;
	disabled?: boolean;
	isModalOpen?: boolean;
	lockedValues?: FilterOption[];
	size?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * BaseDateRange Component
 *
 * A clean filter component that provides date range selection using the unified DatePicker component.
 */
export const BaseDateRange: React.FC<BaseDateRangeProps> = ({
	filterType,
	config,
	selectedValues,
	onValuesChange,
	disabled = false,
	isModalOpen = false,
	lockedValues = [],
	size = 'md',
}) => {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// Determine label text size based on size prop (matches FormField)
	const labelSizeClass = {
		xs: 'text-xs',
		sm: 'text-sm',
		md: 'text-sm',
		lg: 'text-base',
	}[size];

	// Use ref to avoid dependency on onValuesChange
	const onValuesChangeRef = useRef(onValuesChange);
	onValuesChangeRef.current = onValuesChange;

	// Extract current date range from selected values
	const currentRange = selectedValues[0] || lockedValues[0];
	const isLocked = lockedValues.length > 0;

	// Extract dates from the current range. FilterOption.data is untyped
	// (unknown); date-range values carry the shape written by
	// handleDateChange below.
	const rangeData = currentRange?.data as
		| { startDate?: string | null; endDate?: string | null }
		| undefined;
	const startDate = rangeData?.startDate
		? new Date(`${rangeData.startDate}T12:00:00`)
		: null;
	const endDate = rangeData?.endDate
		? new Date(`${rangeData.endDate}T12:00:00`)
		: null;

	// Format date for API
	const formatDateForAPI = (date: Date): string => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	// Format date for display
	const formatDateForDisplay = (date: Date): string =>
		date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});

	// Handle date range selection
	const handleDateChange = (dates: [Date | null, Date | null]) => {
		const [start, end] = dates;

		if (start && end) {
			// Both dates selected - create the range
			const label = `${formatDateForDisplay(start)} - ${formatDateForDisplay(end)}`;
			const newValue: FilterOption = {
				id: 'custom',
				label,
				data: {
					startDate: formatDateForAPI(start),
					endDate: formatDateForAPI(end),
				},
			};
			onValuesChangeRef.current([newValue]);
		} else if (start || end) {
			// Partial selection - update but allow completion
			const label = start
				? formatDateForDisplay(start)
				: end
					? formatDateForDisplay(end)
					: '';
			const newValue: FilterOption = {
				id: 'custom',
				label,
				data: {
					startDate: start ? formatDateForAPI(start) : null,
					endDate: end ? formatDateForAPI(end) : null,
				},
			};
			onValuesChangeRef.current([newValue]);
		} else {
			// Clear the selection
			onValuesChangeRef.current([]);
		}
	};

	// Auto-select default value if nothing is selected
	useEffect(() => {
		if (!currentRange && config.defaultValue) {
			onValuesChangeRef.current([config.defaultValue]);
		}
	}, [currentRange, config.defaultValue]);

	return (
		<div className="mb-4">
			{config.label && (
				<Text
					as="label"
					className={cn(
						'block font-medium mb-2',
						labelSizeClass,
						colors.classes.textPrimary,
					)}
				>
					{t(config.label)}
					{config.required && (
						<Text as="span" className={cn(colors.classes.textDanger, 'ml-1')}>
							*
						</Text>
					)}
				</Text>
			)}

			<div className="flex items-center gap-2">
				{/* Lock icon for locked filters */}
				{isLocked && (
					<FontAwesomeIcon
						icon={faLock}
						className={cn('w-4 h-4', colors.classes.textSecondary)}
					/>
				)}

				{/* Unified Date Range Picker */}
				<div className="flex-1">
					<DatePicker
						mode="range"
						startDate={startDate}
						endDate={endDate}
						onChange={handleDateChange}
						disabled={disabled || isModalOpen || isLocked}
						placeholderText={t('Select date range...')}
						fullWidth={true}
						allowClear={config.allowClear}
					/>
				</div>
			</div>
		</div>
	);
};
